<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	// 	Svelte
	import { getContext, onDestroy } from 'svelte';
	//Hooks
	import { slide } from 'svelte/transition';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar, openExternalLink } from '../../../helpers/utilities';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { readDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Button, Checkbox, Text, Select, Input, Alert, Icon } from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	//Types
	import type { SettingGen2RebootRequest } from '../../../types/requests/settings/setting_g2_reboot';
	import type { Device } from '../../../types/Device';

	interface mqttConfig {
		enable: boolean;
		server?: string | null;
		client_id?: string | null;
		user?: string | null;
		pass?: string | null;
		ssl_ca?: string | null;
		rpc_ntf?: boolean;
		status_ntf?: boolean;
		topic_prefix?: string | null;
	}

	$: statusStore = getDeviceStatus(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		errorsLabels = {
			topic_prefix_invalid_chars: $format('tpc_prf_invalid_chars', {
				default: 'Could not start with $ and #, +, %, ? are not allowed.',
			}),
			topic_prefix_max_length: $format('tpc_prf_info_toast', {
				default: 'Topic prefix can not be over 300 symbols',
			}),
			server: $format('mqtt_server_err_toast', {
				default: 'Server can not be empty.',
			}),
		},
		connect = async () => {
			waitForResponse = true;
			const params = { config },
				res: SettingGen2RebootRequest = await gen2Request(device, 'mqtt.setconfig', params);
			if (res.isok) {
				const settings = { ...readDeviceSettings(id) };
				settings.mqtt = params.config;
				devices_settings.update_devices_settings(id, settings);
			}
			onSettingsResponse(res, device, {
				success: $format('mqtt_scc_toast', {
					default: 'MQTT settings changed successfully, device reboot is required.',
				}),
			});
			waitForResponse = false;
		},
		validate = () => {
			config = { enable: mqtt.enable };
			if (mqtt.enable) {
				if (mqtt.topic_prefix) {
					if (/(^[$|#]|[+%?])/i.test(mqtt.topic_prefix)) {
						errors.topic_prefix_invalid_chars = true;
						return;
					}
					if (mqtt.topic_prefix.length > 300) {
						errors.topic_prefix_max_length = true;
						return;
					}
				}
				if (!mqtt.server) {
					errors.server = true;
					return;
				}
				config = mqtt;
				config.ssl_ca = ssl_ca_enable ? mqtt.ssl_ca : null;
			}
			modal.open();
		};

	let mqtt: mqttConfig = { ...readDeviceSettings(id)?.mqtt },
		config: mqttConfig,
		options = ['*', 'ca.pem', 'user_ca.pem'],
		errors = {
			topic_prefix_max_length: false,
			server: false,
			topic_prefix_invalid_chars: false,
		},
		modal: NetworkModal,
		ssl_ca_enable = Boolean(mqtt.ssl_ca),
		waitForResponse = false;

	let moreInfoStr = `${$format('about_mqtt_txt', {
		default: 'About MQTT protocol',
	})}`;
	let splittedMoreInfStr = moreInfoStr.split(' ');

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Checkbox size="l" class="mb-s" bind:checked={mqtt.enable} disabled={isOffline} id="checkbox-rfrw6sf">
	{capFirstChar(
		$showNewLayout
			? $format('sub_settings_title_mqtt', { default: 'mqtt' })
			: $format('option_enable', { default: 'enable' }),
	)}
</Checkbox>

<hr class="mb-m" />

<NetworkConnectedStatus connected={$statusStore?.mqtt?.connected} />

<Text class="mb-s" styleType="body-small">
	{$format('mqtt_description', {
		default: 'Configure the Shelly device to execute actions via MQTT.',
	})}
</Text>

<Alert
	type="warning"
	isHeadingBold
	heading={$format('label_info')}
	class="mb-s"
	message={$format('mqtt_warning', {
		default: 'WARNING: Changing these settings requires a device reboot!',
	})}
/>
<Text styleType="body-small" class="mt-s mb-s">
	{$format('mqtt_connection_type', { default: 'Connection type' })}
</Text>

<Checkbox
	disabled={!mqtt.enable || isOffline}
	size="m"
	bind:checked={mqtt.rpc_ntf}
	id="checkbox-rtfaj54"
>
	{$format('mqtt_notifications', {
		default: 'RPC status notifications over MQTT:',
	})}
</Checkbox>

<Checkbox
	disabled={!mqtt.enable || isOffline}
	size="m"
	bind:checked={mqtt.status_ntf}
	id="checkbox-tet096o"
>
	{$format('mqtt_status', { default: 'Generic status update over MQTT:' })}
</Checkbox>

<Checkbox
	disabled={!mqtt.enable || isOffline}
	size="m"
	bind:checked={ssl_ca_enable}
	id="checkbox-vnx0n33"
>
	{$format('ws_outbound_desc', { default: 'SSL connectivity' })}
</Checkbox>

{#if ssl_ca_enable}
	<div transition:slide={{ duration: 200 }}>
		<Select
			aria-label={$format('select_ssl_certificate', {
				default: 'Select SSL certificate',
			})}
			size="m"
			class="mb-s"
			bind:value={mqtt.ssl_ca}
			{options}
			icon="fas fa-file-certificate"
			disabled={isOffline}
			id="select-rhnym1s"
		/>
	</div>
{/if}

<Input
	disabled={!mqtt.enable || isOffline}
	size="m"
	class="mb-s"
	label={$format('input_label_mgtt_placeholder', { default: 'MQTT prefix' })}
	bind:value={mqtt.topic_prefix}
	icon="fa fa-input-text"
	error={errors.topic_prefix_max_length
		? errorsLabels.topic_prefix_max_length
		: errors.topic_prefix_invalid_chars
			? errorsLabels.topic_prefix_invalid_chars
			: ''}
	on:input={() => {
		errors.topic_prefix_max_length = false;
		errors.topic_prefix_invalid_chars = false;
	}}
	id="input-tc5be2d"
	aria-labelledby="input-tc5be2d"
/>

<Input
	disabled={!mqtt.enable || isOffline}
	size="m"
	class="mb-s"
	label="Eg: my-server.com:1883"
	bind:value={mqtt.server}
	icon="far fa-server"
	autocomplete="nope"
	error={errors.server ? errorsLabels.server : ''}
	on:input={() => (errors.server = false)}
	id="input-7hlkb6o"
	aria-labelledby="input-7hlkb6o"
/>

<Input
	disabled={!mqtt.enable || isOffline}
	size="m"
	class="mb-s"
	label={$format('input_label_client_id_placeholder', { default: 'client id' })}
	bind:value={mqtt.client_id}
	icon="fa fa-input-text"
	id="input-axetzr2"
	aria-labelledby="input-axetzr2"
/>

<Input
	disabled={!mqtt.enable || isOffline}
	size="m"
	class="mb-s"
	label={capFirstChar($format('placeholder_username', { default: 'username' }))}
	bind:value={mqtt.user}
	icon="fas fa-user"
	autocomplete="nope"
	id="input-yaxi8pm"
	aria-labelledby="input-yaxi8pm"
/>

<Input
	a11yProps={{
		iconButtonAriaLabel: capFirstChar($format('label_password', { default: 'password' })),
	}}
	disabled={!mqtt.enable || isOffline}
	size="m"
	class="mb-m"
	label={capFirstChar($format('label_password', { default: 'Password' }))}
	bind:value={mqtt.pass}
	type="password"
	icon="fas fa-lock-keyhole"
	autocomplete="new-password"
	id="input-9htb1ct"
	aria-labelledby="input-9htb1ct"
/>

{#if !$showNewLayout}
	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	title={mqtt.enable
		? $format('enable_mqtt_title', {
				default: 'Enable MQTT?',
			})
		: $format('disable_mqtt_title', {
				default: 'Disable MQTT?',
			})}
	btnTexts={{
		cancelText: '',
		saveText: mqtt.enable ? $format('button_enable') : $format('disable'),
	}}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
>
	{#if mqtt.enable}
		{$format('msg_enable_actions_mqtt', {
			default:
				'Enable the Shelly device to execute actions via MQTT. Are you sure you want to proceed?',
		})}
	{:else}
		{$format('msg_disable_actions_mqtt', {
			default:
				'Disable the Shelly device to execute actions via MQTT. Are you sure you want to proceed?',
		})}
	{/if}
	<div class="d-flex align-items-center mt-m" slot="additionalInfo">
		<Text styleType="body-small">
			{$format('need_more_inf_txt', { default: 'Need more info?' })}
			<Text
				class="link-styles link-underline d-inline"
				target="_blank"
				styleType="body-small"
				on:click={() => {
					openExternalLink(
						'https://shelly-api-docs.shelly.cloud/gen2/ComponentsAndServices/Mqtt/',
					);
				}}
			>
				<span class="d-inline-block">
					<Icon
						size="s"
						class="text-primary"
						icon="fa-regular fa-arrow-up-right-from-square"
					/>
					{splittedMoreInfStr[0]}
				</span>
				{splittedMoreInfStr.slice(1).join(' ')}
			</Text>
		</Text>
	</div>
</NetworkModal>
